import type {
  ApplicationStatus,
  AwardStatus,
  Scholarship,
  ScholarshipApplication,
  ScholarshipAward,
  ScholarshipPayment,
  ScholarshipRenewal,
} from "../../services/scholarshipService";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Scholarships: what the school offers, families' applications, the decisions
 * on them, the awards and what has been paid out of each, and renewals. The
 * decisions here mirror the server's transitions (review, transition, cancel).
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const ugx = (value?: string | number | null) => `UGX ${Math.round(Number(value ?? 0)).toLocaleString()}`;

export const TYPES: [string, string][] = [
  ["academic", "Academic excellence"],
  ["need_based", "Need-based"],
  ["merit", "Merit-based"],
  ["sports", "Sports"],
  ["arts", "Arts"],
  ["leadership", "Leadership"],
  ["community_service", "Community service"],
  ["other", "Other"],
];
export const COVERAGE: [string, string][] = [
  ["tuition", "Tuition only"],
  ["fees", "Fees only"],
  ["full", "Full coverage"],
  ["partial", "Partial coverage"],
  ["other", "Other"],
];
export const PAYMENT_METHODS: [string, string][] = [
  ["bank_transfer", "Bank transfer"],
  ["check", "Cheque"],
  ["cash", "Cash"],
  ["other", "Other"],
];
const label = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? (value || "—");
export const typeLabel = (value?: string | null) => label(TYPES, value);
export const coverageLabel = (value?: string | null) => label(COVERAGE, value);
export const methodLabel = (value?: string | null) => label(PAYMENT_METHODS, value);

type Tone = { label: string; dot: string };

export function scholarshipState(row: Pick<Scholarship, "is_active" | "is_available" | "application_deadline">, today: string): Tone {
  if (!row.is_active) return { label: "Retired", dot: "bg-gray-400" };
  if (!row.is_available) return { label: "Closed to applications", dot: "bg-gray-400" };
  if (row.application_deadline < today) return { label: "Deadline passed", dot: "bg-amber-500" };
  return { label: "Open", dot: "bg-emerald-500" };
}

export const APPLICATION_STATUS: Record<ApplicationStatus, Tone> = {
  draft: { label: "Draft", dot: "bg-gray-400" },
  submitted: { label: "Submitted", dot: "bg-amber-500" },
  under_review: { label: "Under review", dot: "bg-sky-500" },
  waitlisted: { label: "Waiting list", dot: "bg-violet-500" },
  approved: { label: "Approved", dot: "bg-emerald-500" },
  rejected: { label: "Not successful", dot: "bg-red-500" },
  cancelled: { label: "Withdrawn", dot: "bg-gray-400" },
};
export const applicationStatus = (status: ApplicationStatus) => APPLICATION_STATUS[status] ?? { label: status, dot: "bg-gray-400" };

export const AWARD_STATUS: Record<AwardStatus, Tone> = {
  active: { label: "Active", dot: "bg-emerald-500" },
  suspended: { label: "Suspended", dot: "bg-amber-500" },
  terminated: { label: "Ended early", dot: "bg-red-500" },
  completed: { label: "Completed", dot: "bg-sky-500" },
};
export const awardStatus = (status: AwardStatus) => AWARD_STATUS[status] ?? { label: status, dot: "bg-gray-400" };

export const RENEWAL_STATUS: Record<ScholarshipRenewal["status"], Tone> = {
  pending: { label: "Waiting for a decision", dot: "bg-amber-500" },
  approved: { label: "Renewed", dot: "bg-emerald-500" },
  conditional: { label: "Renewed on conditions", dot: "bg-sky-500" },
  rejected: { label: "Not renewed", dot: "bg-red-500" },
};
export const renewalStatus = (status: ScholarshipRenewal["status"]) => RENEWAL_STATUS[status] ?? { label: status, dot: "bg-gray-400" };

export const PENDING: ApplicationStatus[] = ["submitted", "under_review", "waitlisted"];

/** The decisions a reviewer can make on an application, as the server allows them. */
export function reviewOptions(status: ApplicationStatus) {
  const all = [
    { value: "approved", label: "Approve", help: "The application can then be given an award." },
    { value: "waitlisted", label: "Put on the waiting list", help: "Kept in reserve if places or funds come free." },
    { value: "rejected", label: "Turn down", help: "The family sees the reason you give." },
    { value: "under_review", label: "Take under review", help: "Marks it as being looked at; no reason needed." },
  ];
  const allowed: Record<string, string[]> = {
    submitted: ["approved", "waitlisted", "rejected", "under_review"],
    under_review: ["approved", "waitlisted", "rejected"],
    waitlisted: ["approved", "rejected", "under_review"],
  };
  return all.filter((option) => allowed[status]?.includes(option.value));
}

/** Where an award can go from here. */
export function awardTransitions(status: AwardStatus) {
  const options: Record<AwardStatus, { value: AwardStatus; label: string; help: string }[]> = {
    active: [
      { value: "suspended", label: "Suspend", help: "No disbursements until it is reinstated." },
      { value: "completed", label: "Mark completed", help: "The award has run its course." },
      { value: "terminated", label: "End early", help: "The award stops for good." },
    ],
    suspended: [
      { value: "active", label: "Reinstate", help: "Disbursements can resume." },
      { value: "terminated", label: "End early", help: "The award stops for good." },
    ],
    terminated: [],
    completed: [],
  };
  return options[status];
}

/** How much more can be paid out of an award now, or why none can. */
export function payable(award: Pick<ScholarshipAward, "status" | "award_amount" | "paid_total" | "balance">) {
  const balance = Number(award.balance ?? Number(award.award_amount) - Number(award.paid_total ?? 0));
  if (award.status !== "active") return { amount: 0, reason: "Only an active award can be paid out." };
  if (balance <= 0) return { amount: 0, reason: "The award has been paid in full." };
  return { amount: balance, reason: "" };
}

export const paidShare = (award: Pick<ScholarshipAward, "award_amount" | "paid_total">) => {
  const total = Number(award.award_amount);
  return total > 0 ? Math.min(100, Math.round((Number(award.paid_total ?? 0) / total) * 100)) : 0;
};

// ---- Registers ---------------------------------------------------------------

export function scholarshipConfig(today: string): RegisterConfig<Scholarship> {
  return {
    search: (row) => `${row.name} ${row.sponsor ?? ""} ${row.eligibility_criteria} ${typeLabel(row.scholarship_type)}`,
    views: [
      { key: "open", label: "Open", match: (row) => scholarshipState(row, today).label === "Open" },
      { key: "closed", label: "Closed", match: (row) => row.is_active && scholarshipState(row, today).label !== "Open" },
      { key: "retired", label: "Retired", match: (row) => !row.is_active },
      { key: "all", label: "All" },
    ],
    date: { label: "Deadline", value: (row) => row.application_deadline },
    category: { label: "Kind", value: (row) => typeLabel(row.scholarship_type) },
    sorts: [
      { key: "deadline", label: "Deadline soonest", compare: byText((row) => row.application_deadline) },
      { key: "waiting", label: "Most applications waiting", compare: reversed(byNumber((row) => row.pending_count)) },
      { key: "amount", label: "Largest amount", compare: reversed(byNumber((row) => row.amount)) },
      { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
    ],
  };
}

export const applicationConfig: RegisterConfig<ScholarshipApplication> = {
  search: (row) => `${row.student_name} ${row.student_number ?? ""} ${row.scholarship_name} ${row.class_name ?? ""}`,
  views: [
    { key: "pending", label: "To decide", match: (row) => PENDING.includes(row.status) },
    { key: "to_award", label: "Approved, no award yet", match: (row) => row.status === "approved" && !row.award_id },
    { key: "approved", label: "Approved", match: (row) => row.status === "approved" },
    { key: "closed", label: "Not successful or withdrawn", match: (row) => row.status === "rejected" || row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Applied", value: (row) => row.application_date },
  category: { label: "Scholarship", value: (row) => row.scholarship_name },
  sorts: [
    { key: "oldest", label: "Oldest first", compare: byText((row) => row.application_date) },
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.application_date)) },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
  ],
};

export const awardConfig: RegisterConfig<ScholarshipAward> = {
  search: (row) => `${row.application_name} ${row.student_number ?? ""} ${row.scholarship_name} ${row.class_name ?? ""}`,
  views: [
    { key: "active", label: "Active", match: (row) => row.status === "active" },
    { key: "unpaid", label: "Still to pay out", match: (row) => row.status === "active" && Number(row.balance ?? 0) > 0 },
    { key: "suspended", label: "Suspended", match: (row) => row.status === "suspended" },
    { key: "ended", label: "Completed or ended", match: (row) => row.status === "completed" || row.status === "terminated" },
    { key: "all", label: "All" },
  ],
  date: { label: "Awarded", value: (row) => row.award_date },
  category: { label: "Scholarship", value: (row) => row.scholarship_name },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.award_date)) },
    { key: "balance", label: "Most left to pay", compare: reversed(byNumber((row) => row.balance)) },
    { key: "expiry", label: "Expiring soonest", compare: byText((row) => row.expiry_date || "9999") },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.application_name) },
  ],
};

export const paymentConfig: RegisterConfig<ScholarshipPayment> = {
  search: (row) => `${row.award_name} ${row.student_number ?? ""} ${row.scholarship_name ?? ""} ${row.reference_number} ${row.processed_by_name}`,
  views: [{ key: "all", label: "All" }],
  date: { label: "Paid", value: (row) => row.payment_date },
  category: { label: "Method", value: (row) => methodLabel(row.payment_method) },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.payment_date)) },
    { key: "amount", label: "Largest first", compare: reversed(byNumber((row) => row.amount)) },
  ],
};

export const renewalConfig: RegisterConfig<ScholarshipRenewal> = {
  search: (row) => `${row.award_name} ${row.scholarship_name ?? ""} ${row.renewal_period}`,
  views: [
    { key: "pending", label: "To decide", match: (row) => row.status === "pending" },
    { key: "renewed", label: "Renewed", match: (row) => row.status === "approved" || row.status === "conditional" },
    { key: "rejected", label: "Not renewed", match: (row) => row.status === "rejected" },
    { key: "all", label: "All" },
  ],
  date: { label: "Reviewed for", value: (row) => row.renewal_date },
  sorts: [
    { key: "oldest", label: "Oldest first", compare: byText((row) => row.renewal_date) },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.award_name) },
  ],
};
